import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { api, errMsg } from "@/lib/api";

export function calcEdad(fecha) {
  if (!fecha) return null;
  const d = new Date(fecha + "T00:00:00");
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) a--;
  return a;
}

const EMPTY_SUBJ = { meds: "", last_application: "", before_labs: "", peso: "", estatura: "", changes: "" };

/** Compone el texto de información subjetiva a partir de las respuestas estructuradas. */
export function buildSubjective(subj) {
  return [
    `1. Medicamentos actuales (incluyendo THAG y cómo lo toma): ${subj.meds}`,
    `2. Fecha de última aplicación: ${subj.last_application}`,
    `3. Fecha de aplicación antes de laboratorios: ${subj.before_labs}`,
    `4. Peso y estatura actuales: ${subj.peso} / ${subj.estatura}`,
    `5. Cambios en este tiempo y cómo se ha sentido: ${subj.changes}`,
  ].join("\n");
}

/** Estado y acciones del wizard de nueva solicitud (incluye modo ?continuar={id}).
 *  El paciente puede ir atrás y adelante: los cambios de pasos 1-3 se guardan
 *  (POST al crear, PUT en cada avance posterior) mientras la solicitud sea editable. */
export function useWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [pros, setPros] = useState([]);
  const [selectedPro, setSelectedPro] = useState(null);
  const [personal, setPersonal] = useState({ nombres: "", apellidos: "", fecha_nacimiento: "" });
  const [whatsapp, setWhatsapp] = useState("");
  const [subj, setSubj] = useState(EMPTY_SUBJ);
  const [budget, setBudget] = useState("");
  const [requestId, setRequestId] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const edad = useMemo(() => calcEdad(personal.fecha_nacimiento), [personal.fecha_nacimiento]);
  const continuar = params.get("continuar");

  useEffect(() => {
    api.get("/professionals").then((r) => setPros(r.data)).catch(() => {});
  }, []);

  // Precarga el WhatsApp guardado en el perfil del paciente
  useEffect(() => {
    api.get("/auth/me").then((r) => setWhatsapp((w) => w || r.data?.whatsapp || "")).catch(() => {});
  }, []);

  useEffect(() => {
    if (!continuar) return;
    api
      .get(`/requests/${continuar}`)
      .then((r) => {
        const req = r.data;
        if (req.status !== "pending_payment" && req.status !== "payment_rejected") {
          navigate(`/solicitud/${req.id}`, { replace: true });
          return;
        }
        setRequestId(req.id);
        setPersonal({
          nombres: req.personal.nombres,
          apellidos: req.personal.apellidos,
          fecha_nacimiento: req.personal.fecha_nacimiento,
        });
        const details = req.subjective_details || {};
        const hasDetails = Object.values(details).some(Boolean);
        setSubj(hasDetails ? { ...EMPTY_SUBJ, ...details } : { ...EMPTY_SUBJ, changes: req.subjective || "" });
        setBudget(req.monthly_budget || "");
        setFiles(req.files || []);
        setSelectedPro({
          user_id: req.professional_id,
          name: req.professional_name,
          payment_link: req.professional_payment_link || "",
        });
        setStep(req.status === "payment_rejected" ? 5 : 4);
      })
      .catch((e) => console.warn("No se pudo cargar la solicitud a continuar:", e?.message));
  }, [continuar, navigate]);

  // Guarda (crea o actualiza) la solicitud al avanzar del paso 3
  const saveAndContinue = async () => {
    setBusy(true);
    const payload = {
      professional_id: selectedPro.user_id,
      personal,
      subjective: buildSubjective(subj),
      subjective_details: subj,
      monthly_budget: budget,
    };
    try {
      // Guarda el WhatsApp en el perfil del paciente (visible para el profesional)
      await api.put("/auth/profile", { whatsapp });
      if (requestId) {
        const { data } = await api.put(`/requests/${requestId}`, payload);
        if (data.professional_payment_link !== undefined) {
          setSelectedPro((p) => ({ ...p, payment_link: data.professional_payment_link }));
        }
      } else {
        const { data } = await api.post("/requests", payload);
        setRequestId(data.id);
      }
      setStep(4);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/requests/${requestId}/submit`);
      toast.success("Solicitud enviada a tu especialista");
      navigate(`/solicitud/${requestId}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return {
    step, setStep, pros, selectedPro, setSelectedPro, personal, setPersonal,
    whatsapp, setWhatsapp,
    subj, setSubj, budget, setBudget, requestId, files, setFiles,
    busy, edad, saveAndContinue, submit,
  };
}
